import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { useNotification } from "providers/NotificationProvider";
import {
    formatLocalDateTime,
    LOCAL_DATETIME_MEDIUM_SHORT,
} from "lib/utils/dateTimeDisplay";

import FunctionalButton from "../FunctionalButton";

import { ReactComponent as InfoIcon } from "assets/svgs/toast/Info.svg";
import { ReactComponent as WarningIcon } from "assets/svgs/toast/Warning.svg";

const NotificationRow = ({ notification }) => {
    let icon;
    switch (notification.type) {
        case "warning":
            icon = <WarningIcon className="mt-0.5 w-5 shrink-0 text-yellow-400" />;
            break;
        case "error":
            icon = <WarningIcon className="mt-0.5 w-5 shrink-0 text-warning" />;
            break;
        case "success":
            icon = <InfoIcon className="mt-0.5 w-5 shrink-0 text-primary" />;
            break;
        default:
            icon = <InfoIcon className="mt-0.5 w-5 shrink-0 text-primary" />;
    }

    const absoluteTime = formatLocalDateTime(notification.time, LOCAL_DATETIME_MEDIUM_SHORT);

    return (
        <div className="flex w-full min-w-0 items-start gap-x-3 border-b border-black40 px-2 py-3 last:border-b-0">
            {icon}
            <div className="flex h-fit min-w-0 flex-1 flex-col items-stretch justify-start space-y-1">
                <p
                    className="no-scrollbar max-w-full overflow-x-auto overscroll-x-contain whitespace-nowrap text-sm"
                    title={notification.message}
                >
                    {notification.message}
                </p>
                <p className="shrink-0 text-xs text-black40">{absoluteTime}</p>
            </div>
        </div>
    );
};

const NotificationPanel = ({ className = "" }) => {
    const { t } = useTranslation(["control", "common"]);
    const { notifications, clearNotifications } = useNotification();
    const [tabType, setTabType] = useState("");

    const computedNotifications = useMemo(() => {
        const filtered =
            tabType === ""
                ? notifications
                : notifications.filter((notification) => notification.type === tabType);
        return [...filtered].sort((a, b) => b.time - a.time);
    }, [notifications, tabType]);

    const typeTabs = useMemo(
        () =>
            notifications.reduce((result, notification) => {
                if (!result.includes(notification.type)) result.push(notification.type);
                return result;
            }, []),
        [notifications]
    );

    return (
        <div
            className={twMerge(
                "z-50 flex h-80 w-[18rem] flex-col items-center justify-start space-y-2 rounded-r5 bg-chrome p-4 text-white shadow-lg",
                className
            )}
            role="dialog"
            aria-label={t("notifications.title")}
        >
            <div className="flex w-full items-baseline justify-between">
                <p>{t("notifications.title")}</p>
                <FunctionalButton
                    titleClassName="text-black40 text-xs"
                    title={t("notifications.clearAll")}
                    disabled={notifications.length === 0}
                    onClick={() => clearNotifications()}
                />
            </div>
            <div className="flex h-full w-full flex-col items-center justify-start">
                <div className="scroll-horizontal flex w-full items-center justify-start space-x-2">
                    <div
                        className="flex cursor-pointer items-center space-x-1"
                        onClick={() => setTabType("")}
                    >
                        <p className={`text-sm ${tabType === "" ? "text-white" : "text-black40"}`}>
                            {t("common:all")}
                        </p>
                        <div className="flex h-4 w-fit items-center rounded-r3 bg-primary px-1 text-center">
                            <p className="text-sm">{notifications.length}</p>
                        </div>
                    </div>
                    {typeTabs.map((type) => (
                        <div
                            key={type}
                            className="flex cursor-pointer items-center space-x-1"
                            onClick={() => setTabType(type)}
                        >
                            <p
                                className={`text-sm capitalize ${
                                    tabType === type ? "text-white" : "text-black40"
                                }`}
                            >
                                {type}
                            </p>
                            <div className="flex h-4 w-fit items-center rounded-r3 bg-primary px-1 text-center">
                                <p className="text-sm">
                                    {
                                        notifications.filter(
                                            (notification) => type === notification.type
                                        ).length
                                    }
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="no-scrollbar mt-2 max-h-[calc(100%-4rem)] w-full min-w-0 overflow-x-auto overflow-y-auto overscroll-contain">
                    {computedNotifications.length === 0 ? (
                        <p className="px-2 py-6 text-center text-sm text-black40">
                            {t("notifications.empty")}
                        </p>
                    ) : (
                        computedNotifications.map((notification) => (
                            <NotificationRow key={notification.id} notification={notification} />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationPanel;
